import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Badge,
  Avatar,
  Divider,
  Grid
} from "@mui/material";
import { Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BookingImage from "../../icons/Booking.svg";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { CSSTransition } from "react-transition-group";
import styles from "./BookingForm.module.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Tooltip } from "@mui/material";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete"; // Import DeleteIcon
import axios from "axios";

const StyledTabs = styled((props) => (
  <Tabs {...props} TabIndicatorProps={{ style: { display: "none" } }} />
))({
  minHeight: "unset",
  "& .MuiTabs-flexContainer": {
    flexWrap: "wrap"
  }
});

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    minWidth: "60px",
    padding: "4px 8px",
    fontSize: "0.8rem",
    borderRadius: "20px",
    margin: "4px",
    background: "#fff",
    color: "#000",
    "&:hover": {
      background: "#f0f0f0"
    },
    "&.Mui-selected": {
      background: "linear-gradient(to right, #353943, #00338d)",
      color: "#fff"
    },
    "&.Mui-selected:hover": {
      background: "linear-gradient(to right, #353943, #00338d)"
    }
  })
);

const StyledListItem = styled("div")(
  ({ theme, selected, disabled, isAvailable }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: selected
      ? "linear-gradient(to right, #353943, #00338d)"
      : disabled
        ? "#e0e0e0"
        : "#fff",
    color: selected ? "#fff" : disabled ? "#9e9e9e" : "#000",
    "&:hover": {
      background: selected
        ? "linear-gradient(to right, #353943, #00338d)"
        : disabled
          ? "#e0e0e0"
          : "#f9f9f9"
    },
    borderRadius: "10px",
    marginRight: "10px",
    flex: "0 0 auto",
    padding: "10px",
    maxWidth: "180px",
    boxSizing: "border-box",
    opacity: disabled ? 0.6 : 1,
    pointerEvents: disabled ? "none" : "auto",
    cursor: disabled ? "not-allowed" : "pointer",
    "&:hover": {
      background: selected
        ? "linear-gradient(to right, #353943, #00338d)"
        : disabled
          ? "#e0e0e0"
          : "#f9f9f9"
    },
    // Ensure all child elements have the correct color when selected
    "& *": {
      color: selected ? "#fff" : disabled ? "#9e9e9e" : undefined
    },
    // Adjust icon color
    "& .MuiSvgIcon-root": {
      color: selected ? "#fff" : undefined
    },
    // Adjust Badge styles when selected
    "& .MuiBadge-badge": {
      backgroundColor: selected ? "#fff" : undefined,
      color: selected ? "#000" : undefined
    }
  })
);
const initialBookingDetails = {
  name: "",
  email: "",
  phone: "",
  tableType: "",
  numberOfPeople: 1,
  notes: "",
  mapName: "",
  tableNumber: ""
};
const getStatusColor = (status) => {
  switch (status) {
    case "Completed":
      return "#4CAF50"; // Green
    case "Pending":
      return "#FF9800"; // Orange
    default:
      return "#9E9E9E"; // Gray
  }
};

// Define styles for the status Chip
const getStatusStyles = (status) => {
  return {
    backgroundColor: getStatusColor(status),
    color: "#fff",
    fontWeight: "bold"
  };
};

const BookingDetailsForm = ({
  selectedBooking,
  setSelectedBooking,
  fetchBookings,
  eventId
}) => {
  console.log("selectedBooking: ", selectedBooking);
  const [bookingDetails, setBookingDetails] = useState({
    name: "",
    email: "",
    phone: "",
    numberOfPeople: 1,
    notes: "",
    price: "", // For 'create' action
    prepayment: "", // For 'create' action
    tableNumber: "", // For 'create' action
    mapName: "" // For 'create' action
  });
  const [errors, setErrors] = useState({});
  const userRole = localStorage.getItem("userRole");
  const canViewPrepayment = (role) => {
    return role === "Admin" || role === "Clubowner";
  };

  const canEditPrepayment = canViewPrepayment;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (selectedBooking) {
      console.log("phone: ", selectedBooking);
      setBookingDetails({
        name: selectedBooking.bookingDetails.namereservation || "",
        email: selectedBooking.bookingDetails.emailreservation || "",
        phone: selectedBooking.bookingDetails.phone || "",
        numberOfPeople:
          Number(selectedBooking.bookingDetails.numberOfPeople) ||
          selectedBooking.seats ||
          1,
        notes: selectedBooking.bookingDetails.notes || "",
        price: selectedBooking.price || 0,
        prepayment: ((selectedBooking.price || 0) * 0.1).toFixed(2),
        tableNumber: selectedBooking.tableNumber || "",
        mapName: selectedBooking.mapName || "" // This should now be defined
      });
    }
  }, [selectedBooking]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingDetails({ ...bookingDetails, [name]: value });
  };

  // BookingDetailsForm.jsx
  const handleCancel = () => {
    setSelectedBooking(null);
    // Reset booking details to initial state
    setBookingDetails({
      name: "",
      email: "",
      phone: "",
      numberOfPeople: 1,
      notes: "",
      price: "",
      prepayment: "",
      tableNumber: "",
      mapName: ""
    });
  };

  const handleSubmit = async (action, e) => {
    console.log("INSIDE IN HERE : ", action);
    e.preventDefault();

    // Retrieve userId and token from localStorage
    const userId = localStorage.getItem("adminId"); // Ensure this key is correct
    const token = localStorage.getItem("token");
    if (action === "delete") {
      // Confirm deletion
      const confirmDelete = window.confirm(
        "Are you sure you want to cancel this booking?"
      );
      if (!confirmDelete) {
        return;
      }
    } else {
      // Validation for 'update' action
      let newErrors = {};

      if (!bookingDetails.name.trim()) {
        newErrors.name = "Name is required";
      }

      // Validate emailreservation
      if (!bookingDetails.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(bookingDetails.email)) {
        newErrors.email = "Email address is invalid";
      }

      // Validate phone
      if (!bookingDetails.phone.trim()) {
        newErrors.phone = "Phone number is required";
      }

      // Validate numberOfPeople
      if (
        !Number.isInteger(Number(bookingDetails.numberOfPeople)) ||
        Number(bookingDetails.numberOfPeople) < 1
      ) {
        newErrors.numberOfPeople = "Must be at least 1 person";
      }

      // Check for errors
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return; // Stop form submission
      } else {
        setErrors({});
      }
    }

    // Prepare data based on action

    let bookingData = {
      mapName: selectedBooking.mapName,
      updatedMaps: [
        {
          mapName: selectedBooking.mapName,
          tables: [
            {
              tableNumber: selectedBooking.tableNumber,
              bookingFee: Number(bookingDetails.prepayment), // Include bookingFee
              bookingDetails:
                action === "delete"
                  ? {}
                  : {
                      namereservation: bookingDetails.name,
                      emailreservation: bookingDetails.email,
                      phone: bookingDetails.phone,
                      numberOfPeople: Number(bookingDetails.numberOfPeople),
                      notes: bookingDetails.notes
                      // Do not include paymentStatus here
                    }
            }
          ]
        }
      ],
      id: userId,
      action: action // 'update' or 'delete'
    };
    console.log("MAP NAME FINAL : ", bookingDetails);
    console.log("Final booking data:", bookingData);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/tables/event-tables/update/${eventId}`,
        bookingData,
        {
          headers: {
            authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        alert(
          action === "update"
            ? "Booking updated successfully!"
            : "Booking canceled successfully!"
        );
        setSelectedBooking(null);

        if (typeof fetchBookings === "function") {
          fetchBookings(); // Refresh bookings to reflect changes
        }
      } else {
        alert(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.response?.data || error.message;
      console.error(`Error performing ${action}:`, errorMessage);
      alert(errorMessage);
    }
  };

  if (!selectedBooking) return null;

  return (
    <div style={{ marginBottom: "20px", maxWidth: "600px" }}>
      {/* Back Button */}
      <Button
        variant="outlined"
        onClick={handleCancel}
        sx={{
          marginTop: "20px",
          marginBottom: "20px",
          color: "grey",
          borderColor: "grey",
          backgroundColor: "#FFFF"
        }}
        startIcon={<ArrowBackIcon />}
      />

      <Typography variant="h6" align="left" gutterBottom>
        Editing Booking for Table: {selectedBooking.tableName}
      </Typography>

      <Paper
        elevation={3}
        style={{
          padding: "15px",
          marginTop: "10px",
          borderLeft: `5px solid ${getStatusColor(
            selectedBooking.bookingDetails.paymentStatus
          )}`
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar>
              <EventSeatIcon />
            </Avatar>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">
              Table {selectedBooking.tableNumber} - {selectedBooking.tableName}
            </Typography>
          </Grid>
          <Grid item xs={4} style={{ textAlign: "right" }}>
            <Chip
              label={selectedBooking.bookingDetails.paymentStatus}
              style={getStatusStyles(
                selectedBooking.bookingDetails.paymentStatus
              )}
            />
          </Grid>
        </Grid>
        <Divider style={{ margin: "10px 0" }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" gutterBottom>
              <AssignmentIndIcon fontSize="small" /> Customer Information
            </Typography>
            {selectedBooking ? (
              <>
                <Typography variant="body2">
                  <strong>Name:</strong> {bookingDetails.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {bookingDetails.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {bookingDetails.phone || "N/A"}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">Enter customer details.</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" gutterBottom>
              <PeopleIcon fontSize="small" /> Table Details
            </Typography>
            {selectedBooking ? (
              <>
                <Typography variant="body2">
                  <strong>Number of People:</strong>{" "}
                  {bookingDetails.numberOfPeople}
                </Typography>
                <Typography variant="body2">
                  <strong>Price:</strong> ${selectedBooking.price}
                </Typography>
                <Typography variant="body2">
                  <strong>Prepayment:</strong> $
                  {((selectedBooking.price || 0) * 0.1).toFixed(2)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">Review table details.</Typography>
            )}
          </Grid>
        </Grid>

        <form onSubmit={(e) => handleSubmit("update", e)}>
          {/* Conditionally render fields based on action */}
          {!selectedBooking && (
            <>
              <Typography variant="subtitle1" align="left" gutterBottom>
                Select Table Type
              </Typography>
              {/* Add your table selection logic here */}
              <TextField
                fullWidth
                label="Map Name"
                name="mapName"
                value={bookingDetails.mapName}
                onChange={handleInputChange}
                margin="normal"
                required
                error={Boolean(errors.mapName)}
                helperText={errors.mapName}
              />
              <TextField
                fullWidth
                label="Table Number"
                name="tableNumber"
                value={bookingDetails.tableNumber}
                onChange={handleInputChange}
                margin="normal"
                required
                error={Boolean(errors.tableNumber)}
                helperText={errors.tableNumber}
              />
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={bookingDetails.price}
                onChange={handleInputChange}
                margin="normal"
                required
                error={Boolean(errors.price)}
                helperText={errors.price}
              />
              <TextField
                fullWidth
                label="Prepayment"
                name="prepayment"
                type="number"
                value={bookingDetails.prepayment}
                onChange={handleInputChange}
                margin="normal"
                required
                error={Boolean(errors.prepayment)}
                helperText={errors.prepayment}
              />
            </>
          )}

          {/* Common Fields */}
          <Typography variant="subtitle1" align="left" gutterBottom>
            Booking Name
          </Typography>
          <TextField
            fullWidth
            label="Enter booking name"
            name="name"
            value={bookingDetails.name}
            onChange={handleInputChange}
            margin="normal"
            required
            error={Boolean(errors.name)}
            helperText={errors.name}
          />

          <Box display="flex" justifyContent="space-between" gap={2}>
            <div style={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" align="left" gutterBottom>
                Phone
              </Typography>
              <TextField
                fullWidth
                label="Enter phone number"
                name="phone"
                value={bookingDetails.phone}
                onChange={handleInputChange}
                margin="normal"
                required
                error={Boolean(errors.phone)}
                helperText={errors.phone}
              />
            </div>
            <div style={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" align="left" gutterBottom>
                Email
              </Typography>
              <TextField
                fullWidth
                label="Enter email"
                name="email"
                type="email"
                value={bookingDetails.email}
                onChange={handleInputChange}
                margin="normal"
                required
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </div>
          </Box>

          <Typography variant="subtitle1" align="left" gutterBottom>
            Number of People
          </Typography>
          <TextField
            fullWidth
            label="Number of People"
            name="numberOfPeople"
            type="number"
            value={bookingDetails.numberOfPeople}
            onChange={handleInputChange}
            margin="normal"
            required
            error={Boolean(errors.numberOfPeople)}
            helperText={errors.numberOfPeople}
          />
          {canViewPrepayment(userRole) && (
            <>
              <Typography variant="subtitle1" align="left" gutterBottom>
                Prepayment Price
              </Typography>
              <TextField
                fullWidth
                label="Prepayment"
                name="prepayment"
                value={bookingDetails.prepayment}
                onChange={handleInputChange}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  readOnly: !canEditPrepayment(userRole)
                }}
                required
                error={Boolean(errors.prepayment)}
                helperText={errors.prepayment}
              />
            </>
          )}

          <Typography variant="subtitle1" align="left" gutterBottom>
            Notes
          </Typography>
          <TextField
            fullWidth
            label="Add any special requests or notes"
            name="notes"
            value={bookingDetails.notes}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={3}
          />

          {/* Action Buttons */}
          <Box display="flex" justifyContent="space-between" mt={2}>
            {/* Save Changes Button */}
            <Button
              type="submit"
              variant="contained"
              style={{
                background: "linear-gradient(90deg, #00338D 0%, #353943 100%)",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: "5px",
                transition: "background 0.3s ease",
                flexGrow: 1,
                marginRight: "10px"
              }}
            >
              Save Changes
            </Button>

            {/* Cancel Booking Button */}
            <Button
              type="button"
              variant="outlined"
              color="error"
              style={{
                padding: "10px 20px",
                borderRadius: "5px",
                transition: "background 0.3s ease",
                flexGrow: 1
              }}
              onClick={(e) => handleSubmit("delete", e)}
              startIcon={<DeleteIcon />}
            >
              Cancel Booking
            </Button>
          </Box>
        </form>
      </Paper>
    </div>
  );
};

const Booking = ({
  formVisible,
  setFormVisible,
  selectedTab,
  setSelectedTab,
  maps,
  tableCategories,
  tableDataPerCategory,
  selectedTableType,
  setSelectedTableType,
  bookingDetails,
  setBookingDetails,
  initialBookingDetails,
  handleInputChange,
  handleIncrement,
  handleDecrement,
  handleTabChange,
  handleTableTypeSelect,
  isCategoryAvailable,
  handleSubmit,
  errors
}) => {
  console.log("IN", bookingDetails);
  const userRole = localStorage.getItem("userRole");
  const canViewPrepayment = (role) => {
    return role === "Admin" || role === "Clubowner";
  };

  const canEditPrepayment = canViewPrepayment;
  return (
    <CSSTransition in={formVisible} timeout={0} classNames="form" unmountOnExit>
      <div style={{ marginBottom: "20px", maxWidth: "600px" }}>
        <Button
          variant="outlined"
          onClick={() => {
            setFormVisible(false);
            setSelectedTableType(null);
            setBookingDetails(initialBookingDetails);
          }}
          sx={{
            marginTop: "20px",
            marginBottom: "20px",
            color: "grey",
            borderColor: "grey",
            backgroundColor: "#FFFF"
          }}
          startIcon={<ArrowBackIcon />}
        />

        <StyledTabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {maps.map((map, index) => (
            <StyledTab key={index} label={map.mapName || `Map ${index + 1}`} />
          ))}
        </StyledTabs>
        <div style={{ marginTop: "30px" }}>
          <Typography variant="subtitle1" align="left" gutterBottom>
            Select Table Type
          </Typography>
          {errors.tableType && (
            <Typography variant="body2" color="error">
              {errors.tableType}
            </Typography>
          )}
        </div>

        <div
          style={{
            overflowX: "auto",
            marginBottom: "20px"
          }}
        >
          <div style={{ display: "flex", flexWrap: "nowrap" }}>
            {tableCategories && tableCategories.length > 0 ? (
              tableCategories.map((category) => {
                const tables = tableDataPerCategory[selectedTab][category];
                const isAvailable = isCategoryAvailable(category);
                const tableInfo = tables[0]; // Get the first table in the category
                return (
                  <Tooltip
                    key={category}
                    title={isAvailable ? "" : "Sold Out"}
                    arrow
                  >
                    <StyledListItem
                      selected={selectedTableType === category}
                      onClick={() =>
                        isAvailable && handleTableTypeSelect(category)
                      }
                      disabled={!isAvailable} // Pass disabled prop
                      isAvailable={isAvailable} // Pass isAvailable prop for styling
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        align="center"
                      >
                        {category}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        component="span"
                        align="center"
                      >
                        {`Seats: ${tableInfo.people}`}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        component="span"
                        align="center"
                      >
                        {`Price: $${tableInfo.price}`}
                      </Typography>
                      <Badge
                        badgeContent={tableInfo.people}
                        color="primary"
                        style={{ marginTop: "10px" }}
                      >
                        {tableInfo.people > 1 ? (
                          <PeopleIcon color="action" />
                        ) : (
                          <PersonIcon color="action" />
                        )}
                      </Badge>
                    </StyledListItem>
                  </Tooltip>
                );
              })
            ) : (
              <Typography variant="body2" color="textSecondary">
                No categories available
              </Typography>
            )}
          </div>
        </div>

        {selectedTableType && (
          <>
            <Typography variant="subtitle1" align="left" gutterBottom>
              Selected Table Type: {selectedTableType}
            </Typography>

            <form
              onSubmit={(e) => handleSubmit("create", e)}
              className={styles.bookingFormContainer}
            >
              <Typography variant="subtitle1" align="left" gutterBottom>
                Number of People
              </Typography>
              <Box display="flex" alignItems="center" marginBottom="20px">
                <IconButton
                  onClick={handleDecrement}
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    marginRight: "10px"
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography variant="h6">
                  {bookingDetails.numberOfPeople}
                </Typography>
                <IconButton
                  onClick={handleIncrement}
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    marginLeft: "10px"
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>

              <Typography variant="subtitle1" align="left" gutterBottom>
                Booking Name
              </Typography>
              <TextField
                fullWidth
                label="Enter booking name"
                name="name"
                value={bookingDetails.name}
                onChange={handleInputChange}
                margin="normal"
                required
                error={Boolean(errors.name)}
                helperText={errors.name}
              />

              <Box display="flex" justifyContent="space-between" gap={2}>
                <div style={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" align="left" gutterBottom>
                    Phone
                  </Typography>
                  <TextField
                    fullWidth
                    label="Enter phone number"
                    name="phone"
                    value={bookingDetails.phone}
                    onChange={handleInputChange}
                    margin="normal"
                    required
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                  />
                </div>
                <div style={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" align="left" gutterBottom>
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    label="Enter email"
                    name="email"
                    type="email"
                    value={bookingDetails.email}
                    onChange={handleInputChange}
                    margin="normal"
                    required
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                  />
                </div>
              </Box>

              <Typography variant="subtitle1" align="left" gutterBottom>
                Price
              </Typography>
              <TextField
                fullWidth
                label="Price"
                name="price"
                value={bookingDetails.price}
                type="number" // Set the input type to number
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  readOnly: true
                }}
                required
                error={Boolean(errors.price)}
                helperText={errors.price}
              />

              <Typography variant="subtitle1" align="left" gutterBottom>
                Prepayment Price
              </Typography>
              <TextField
                fullWidth
                label="Prepayment"
                name="prepayment"
                value={bookingDetails.prepayment}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  readOnly: !canEditPrepayment(userRole)
                }}
                required
                error={Boolean(errors.prepayment)}
                helperText={errors.prepayment}
              />

              <Typography variant="subtitle1" align="left" gutterBottom>
                Notes
              </Typography>
              <TextField
                fullWidth
                label="Add any special requests or notes"
                name="notes"
                value={bookingDetails.notes}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={3}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                style={{
                  background:
                    "linear-gradient(90deg, #00338D 0%, #353943 100%)",
                  color: "#fff",
                  marginTop: "20px",
                  padding: "15px",
                  borderRadius: "5px",
                  transition: "background 0.3s ease"
                }}
              >
                Confirm Booking
              </Button>
            </form>
          </>
        )}
      </div>
    </CSSTransition>
  );
};
const BookingForm = ({
  onSubmit,
  tableCategories,
  selectedTab,
  setSelectedTab,
  maps,
  tableDataPerCategory,
  formVisible,
  setFormVisible,
  selectedTable,
  setSelectedTable,
  eventId,
  bookedMaps,
  fetchBookings
}) => {
  const [errors, setErrors] = useState({});
  const [selectedBooking, setSelectedBooking] = useState(null);
  const userRole = localStorage.getItem("userRole");
  const canViewPrepayment = (role) => {
    return role === "Admin" || role === "Clubowner";
  };

  const canEditPrepayment = canViewPrepayment;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [bookingDetails, setBookingDetails] = useState({
    name: "",
    email: "",
    phone: "",
    tableType: "",
    numberOfPeople: 1,
    price: 0,
    prepayment: 0,
    notes: "",
    tableNumber: null,
    mapName: null
  });

  const [selectedTableType, setSelectedTableType] = useState(null);

  useEffect(() => {
    // If no categories are available, hide the form
    if (!tableCategories || tableCategories.length === 0) {
      setFormVisible(false);
    }
  }, [tableCategories]);
  console.log("selectedTab: ", selectedTab, maps);

  useEffect(() => {
    if (selectedTable) {
      console.log("selectedTable: ", selectedTable);
      setBookingDetails((prevDetails) => ({
        ...prevDetails,
        tableType: selectedTable.name,
        price: selectedTable.price || "",
        numberOfPeople: selectedTable.people || 1,
        prepayment: selectedTable.price
          ? (selectedTable.price * 0.1).toFixed(2)
          : "",
        mapName: maps[selectedTab].mapName,
        tableNumber: selectedTable.id // Ensure this matches your table identifier
        // Include other necessary fields
      }));

      setSelectedTableType(selectedTable.name);
    } else {
      setSelectedTableType(null);
      setBookingDetails(initialBookingDetails);
    }
  }, [selectedTable]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingDetails({ ...bookingDetails, [name]: value });
  };

  const handleIncrement = () => {
    setBookingDetails((prevDetails) => ({
      ...prevDetails,
      numberOfPeople: prevDetails.numberOfPeople + 1
    }));
  };

  const handleDecrement = () => {
    setBookingDetails((prevDetails) => ({
      ...prevDetails,
      numberOfPeople: Math.max(1, prevDetails.numberOfPeople - 1)
    }));
  };

  const handleSubmit = async (action, e) => {
    e.preventDefault();

    // Retrieve userId from localStorage
    const userId = localStorage.getItem("adminId");
    const token = localStorage.getItem("token");

    if (action === "delete") {
      // Confirm deletion
      if (!window.confirm("Are you sure you want to cancel this booking?")) {
        return;
      }
    } else {
      // Validation for 'create' and 'update' actions
      let newErrors = {};

      // Validate name
      if (!bookingDetails.name.trim()) {
        newErrors.name = "Name is required";
      }

      // Validate email
      if (!bookingDetails.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(bookingDetails.email)) {
        newErrors.email = "Email address is invalid";
      }

      // Validate phone
      if (!bookingDetails.phone.trim()) {
        newErrors.phone = "Phone number is required";
      }

      // Validate numberOfPeople
      if (
        !Number.isInteger(Number(bookingDetails.numberOfPeople)) ||
        bookingDetails.numberOfPeople < 1
      ) {
        newErrors.numberOfPeople = "Must be at least 1 person";
      }

      // Validate mapName and tableNumber
      if (!bookingDetails.mapName) {
        newErrors.mapName = "Map name is required";
      }
      if (!bookingDetails.tableNumber) {
        newErrors.tableNumber = "Table number is required";
      }

      // Check for errors
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return; // Stop form submission
      } else {
        setErrors({});
      }
    }

    // Prepare data based on action
    console.log("MAP NAME FINAL : ", bookingDetails.mapName);
    let bookingData = {
      id: userId,
      action: action, // 'create', 'update', or 'delete'
      updatedMaps: [
        {
          mapName: bookingDetails.mapName,
          tables: [
            {
              tableNumber: bookingDetails.tableNumber,
              bookingFee: Number(bookingDetails.prepayment), // Include bookingFee
              bookingDetails:
                action === "delete"
                  ? {} // Clear bookingDetails for delete action
                  : {
                      namereservation: bookingDetails.name,
                      emailreservation: bookingDetails.email,
                      phone: bookingDetails.phone,
                      numberOfPeople: Number(bookingDetails.numberOfPeople),
                      notes: bookingDetails.notes
                      // Remove paymentStatus from bookingDetails
                    }
            }
          ]
        }
      ]
    };

    // Send the bookingData to the backend
    console.log("bookingData: ", bookingData);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/tables/event-tables/update/${eventId}`,
        bookingData,
        {
          headers: {
            authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        alert(
          action === "create"
            ? "Booking created successfully!"
            : action === "update"
              ? "Booking updated successfully!"
              : "Booking canceled successfully!"
        );
        setSelectedBooking(null);
        setFormVisible(false);

        if (typeof fetchBookings === "function") {
          fetchBookings();
        }
      } else {
        alert(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.response?.data || error.message;
      console.error(`Error performing ${action}:`, errorMessage);
      alert(errorMessage);
    }
  };

  const handleStartBooking = () => {
    setFormVisible(true);
    setSelectedTable(null);
    setSelectedTableType(null);
    setBookingDetails(initialBookingDetails);
  };
  const isCategoryAvailable = (category) => {
    const tables = tableDataPerCategory[selectedTab][category];
    if (!tables || tables.length === 0) return false;
    return tables.some((table) => table.isAvailable);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    // Reset booking details when map changes
    setBookingDetails({
      name: "",
      email: "",
      phone: "",
      tableType: "",
      numberOfPeople: 1,
      price: "",
      prepayment: "",
      notes: ""
    });
    setSelectedTableType(null);
  };

  const handleTableTypeSelect = (category) => {
    const availableTables = tableDataPerCategory[selectedTab][category].filter(
      (table) => table.isAvailable
    );

    if (availableTables.length > 0) {
      const tableInfo = availableTables[0]; // Get the first available table
      const price = tableInfo.price || "";
      const prepayment = price ? (price * 0.1).toFixed(2) : "";
      setSelectedTableType(category);
      setBookingDetails((prevDetails) => ({
        ...prevDetails,
        tableType: category,
        price: price,
        prepayment: prepayment,
        numberOfPeople: tableInfo.people || 1,
        seats: tableInfo.people || 1,
        mapName: maps[selectedTab].mapName,
        tableNumber: tableInfo.id // Ensure this is the correct identifier
      }));
    } else {
      alert("No available tables for this category.");
    }
  };
  const ShareableLink = ({ eventId }) => {
    const shareableLink = `https://patt.club/Tables.html?eventid=${eventId}&promoterName=PATT_APP`;

    const copyToClipboard = () => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareableLink).then(
          () => {
            alert("Link copied to clipboard");
          },
          (err) => {
            console.error("Could not copy text: ", err);
            alert("Failed to copy the link");
          }
        );
      } else {
        // Fallback method for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = shareableLink;
        // Avoid scrolling to bottom
        textArea.style.top = 0;
        textArea.style.left = 0;
        textArea.style.position = "fixed";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            alert("Link copied to clipboard");
          } else {
            alert("Failed to copy the link");
          }
        } catch (err) {
          console.error("Fallback: Oops, unable to copy", err);
          alert("Failed to copy the link");
        }

        document.body.removeChild(textArea);
      }
    };

    return (
      <Box mt={3} textAlign="left">
        <Typography variant="body1" fontWeight="bold">
          Shareable Link:
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          bgcolor="#fff"
          p={1}
          borderRadius="5px"
          mt={1}
          style={{ width: "100%" }}
        >
          <input
            type="text"
            value={shareableLink}
            readOnly
            style={{
              flex: 1,
              border: "none",
              background: "none",
              outline: "none",
              color: "#ccc"
            }}
          />
          <IconButton style={{ marginLeft: "10px" }} onClick={copyToClipboard}>
            <ContentCopyIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };
  const handleBookingClick = (table) => {
    console.log("PRESSED ON : ", table);
    setSelectedBooking({
      ...table,
      price: table.price || 0,
      seats: table.seats || 1,
      tableName: table.tableName || ""
    });
  };

  return (
    <div style={{ padding: "0", backgroundColor: "transparent" }}>
      {/* Always render the Booking component */}
      {selectedBooking ? (
        <BookingDetailsForm
          selectedBooking={selectedBooking}
          setSelectedBooking={setSelectedBooking}
          fetchBookings={fetchBookings}
          eventId={eventId}
        />
      ) : (
        <>
          <Booking
            formVisible={formVisible}
            setFormVisible={setFormVisible}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            maps={maps}
            tableCategories={tableCategories}
            tableDataPerCategory={tableDataPerCategory}
            selectedTableType={selectedTableType}
            setSelectedTableType={setSelectedTableType}
            bookingDetails={bookingDetails}
            setBookingDetails={setBookingDetails}
            initialBookingDetails={initialBookingDetails}
            handleInputChange={handleInputChange}
            handleIncrement={handleIncrement}
            handleDecrement={handleDecrement}
            handleTabChange={handleTabChange}
            handleTableTypeSelect={handleTableTypeSelect}
            isCategoryAvailable={isCategoryAvailable}
            handleSubmit={handleSubmit}
            errors={errors}
          />

          {/* Initial prompt content */}
          {!formVisible && (
            <div
              style={{
                textAlign: "center",
                maxWidth: "800px",
                marginTop: "30px"
              }}
            >
              {!bookedMaps && !bookedMaps.length > 0 && (
                <>
                  <img
                    src={Booking}
                    style={{
                      width: "200px",
                      height: "150px",
                      marginBottom: "20px"
                    }}
                    alt="Table Booking"
                  />
                  <Typography variant="h5" gutterBottom>
                    Table Bookings
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    style={{ marginBottom: "20px" }}
                  >
                    Make your first reservation or share your link to increase
                    ticket sales
                  </Typography>
                </>
              )}

              <Button
                variant="contained"
                style={{
                  background: "linear-gradient(to right, #353943, #00338d)",
                  color: "#fff",
                  padding: "10px 20px",
                  borderRadius: "10px",

                  transition: "transform 0.3s ease"
                }}
                onClick={handleStartBooking}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.05)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
                startIcon={<AddCircleIcon />}
              >
                Make a Booking
              </Button>
              <Box mt={3} textAlign="left">
                <ShareableLink eventId={eventId} />
              </Box>

              <Typography
                variant="h6"
                gutterBottom
                style={{ marginTop: "20px" }}
              >
                Existing Bookings
              </Typography>
              <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                {bookedMaps && bookedMaps.length > 0 ? (
                  bookedMaps.map((map) => (
                    <div key={map.mapName}>
                      <Typography
                        variant="subtitle1"
                        style={{ marginTop: "20px", textAlign: "left" }}
                      >
                        {map.mapName}
                      </Typography>
                      {map.tables.map((table) => (
                        <Paper
                          key={table.tableNumber}
                          elevation={3}
                          style={{
                            padding: "15px",
                            marginTop: "10px",
                            borderLeft: `5px solid ${getStatusColor(
                              table.bookingDetails.paymentStatus
                            )}`,
                            cursor: "pointer" // Make it clickable
                          }}
                          onClick={() => handleBookingClick(table)}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item>
                              <Avatar>
                                <EventSeatIcon />
                              </Avatar>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="h6">
                                Table {table.tableNumber} - {table.tableName}
                              </Typography>
                            </Grid>
                            <Grid item xs={4} style={{ textAlign: "right" }}>
                              <Chip
                                label={table.bookingDetails.paymentStatus}
                                style={getStatusStyles(
                                  table.bookingDetails.paymentStatus
                                )}
                              />
                            </Grid>
                          </Grid>
                          <Divider style={{ margin: "10px 0" }} />
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                <AssignmentIndIcon fontSize="small" /> Customer
                                Information
                              </Typography>
                              <Typography variant="body2">
                                <strong>Name:</strong>{" "}
                                {table.bookingDetails.namereservation}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Email:</strong>{" "}
                                {table.bookingDetails.emailreservation}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Phone:</strong>{" "}
                                {table.bookingDetails.phone || "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                <PeopleIcon fontSize="small" /> Table Details
                              </Typography>
                              <Typography variant="body2">
                                <strong>Number of People:</strong>{" "}
                                {table.numberOfPeople}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Price:</strong> ${table.price}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Prepayment:</strong> $
                                {(table.price * 0.1).toFixed(2)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </div>
                  ))
                ) : (
                  <Typography>No bookings available.</Typography>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BookingForm;
